import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  CalendarIcon,
  ClockIcon,
  UserGroupIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  EyeIcon,
  PlusIcon,
  ArrowDownTrayIcon,
  BellIcon,
  ShieldExclamationIcon,
  DocumentIcon,
  ClockIcon as ClockIconSolid,
  Squares2X2Icon,
  BuildingOffice2Icon,
  DocumentTextIcon as DocumentIconSolid,
  MagnifyingGlassIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line
} from 'recharts';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import { useTheme } from '../../lib/theme.jsx';
import banner from '../../assets/banner.jpeg';
import { appointmentsAPI, kpisAPI } from '../../lib/api.js';
import { formatDate } from '../../utils/formatters.js';

// Helper function to format phone number for display (show only 10 digits)
const formatPhoneForDisplay = (phoneNumber) => {
  if (!phoneNumber) return '';
  
  // Remove all non-digit characters
  const digitsOnly = phoneNumber.replace(/\D/g, '');
  
  // If it's 12 digits and starts with 91, show only the last 10 digits
  if (digitsOnly.length === 12 && digitsOnly.startsWith('91')) {
    return digitsOnly.substring(2);
  }
  
  // If it's 10 digits, show as is
  if (digitsOnly.length === 10) {
    return digitsOnly;
  }
  
  // For other cases, return the original
  return phoneNumber;
};

// Helpers for live data mapping
const rangeToParams = (range) => {
  if (range === 'today') return { days: 1 };
  if (range === '7d') return { days: 7 };
  if (range === '30d') return { days: 30 };
  return {};
};

const DashboardHome = () => {
  const [dateRange, setDateRange] = useState('today');
  const [selectedApprovals, setSelectedApprovals] = useState([]);
  // Appointments pagination (dashboard only)
  const [apptsPage, setApptsPage] = useState(1);
  const [apptsLimit] = useState(10);
  const { isDark } = useTheme();
  useEffect(() => {
    console.log('DashboardHome rendered');
  }, []);

  // Live queries
  const { data: kpisResponse, isLoading: kpisLoading, isError: kpisError } = useQuery({
    queryKey: ['dashboard', 'kpis', dateRange],
    queryFn: () => kpisAPI.getDaily(rangeToParams(dateRange)),
    refetchInterval: 30000,
  });

  const { data: apptsResponse, isLoading: apptsLoading, isError: apptsError } = useQuery({
    queryKey: ['dashboard', 'appointments', dateRange, 'scheduled', apptsPage, apptsLimit],
    // Backend pagination: page/limit; filter only scheduled
    queryFn: () => appointmentsAPI.getAll({
      ...rangeToParams(dateRange),
      status: 'scheduled',
      page: apptsPage,
      limit: apptsLimit,
    }),
    refetchInterval: 30000,
  });

  // (moved below after KPI total is available)

  // Normalize API data shapes with safe fallbacks
  const appointmentsRaw = Array.isArray(apptsResponse?.data)
    ? apptsResponse?.data
    : (apptsResponse?.data?.data || apptsResponse?.data?.appointments || []);
  // Safety: ensure only scheduled shown even if backend returns more
  const appointmentsData = (appointmentsRaw || []).filter(a => (a?.status || '').toLowerCase() === 'scheduled');


  // KPI summary from backend (orders, circulars, appointments, forms, payslips)
  const KPI_SUMMARY = kpisResponse?.data?.kpis?.summary || kpisResponse?.data?.summary || kpisResponse?.data?.data?.summary || {
    orders: 0,
    circulars: 0,
    appointments: 0,
    forms: 0,
    payslips: 0,
  };

  // Compute KPIs from API or from appointments fallback
  const totalAppointmentsVal =
    kpisResponse?.data?.kpis?.summary?.appointments ??
    kpisResponse?.data?.summary?.appointments ??
    kpisResponse?.data?.data?.summary?.appointments ??
    kpisResponse?.data?.totalAppointments ??
    kpisResponse?.data?.data?.totalAppointments ??
    appointmentsData.length;
  const normalizedStatus = (s) => (s || '').toLowerCase();
  const isPendingLike = (s) => ['pending', 'scheduled', 'pending_clerk', 'pending_officer', 'pending_final'].includes(normalizedStatus(s));
  const pendingApprovalsArr = appointmentsData.filter((a) => isPendingLike(a.status));
  const completedCount = appointmentsData.filter((a) => ['approved', 'completed'].includes(normalizedStatus(a.status))).length;
  const noShowCount = appointmentsData.filter((a) => ['no-show', 'cancelled', 'rejected', 'deferred'].includes(normalizedStatus(a.status))).length;

  // Fetch ALL appointments (no status filter) to build Status Distribution
  const { data: apptsAllResponse } = useQuery({
    queryKey: ['dashboard', 'appointments', dateRange, 'all-statuses', totalAppointmentsVal],
    queryFn: () => appointmentsAPI.getAll({
      ...rangeToParams(dateRange),
      page: 1,
      limit: totalAppointmentsVal, // fetch exactly the known total from backend KPIs
    }),
    enabled: !!totalAppointmentsVal,
    refetchInterval: 30000,
  });

  // All appointments (any status) for the Status Distribution chart
  const appointmentsAllRaw = Array.isArray(apptsAllResponse?.data)
    ? apptsAllResponse?.data
    : (apptsAllResponse?.data?.data || apptsAllResponse?.data?.appointments || []);

  // Charts: build from appointments data as fallback
  const hourlyBuckets = {};
  appointmentsData.forEach((a) => {
    const timeStr = a.appointment_time || a.time || '';
    const time = timeStr.slice(0, 2);
    if (!time) return;
    const label = `${time}:00`;
    if (!hourlyBuckets[label]) hourlyBuckets[label] = { hour: label, today: 0, avg7d: 0 };
    hourlyBuckets[label].today += 1;
  });
  const hourlyData = Object.keys(hourlyBuckets)
    .sort()
    .map((k) => hourlyBuckets[k]);

  const statusCounts = (appointmentsAllRaw || []).reduce((acc, a) => {
    const s = (normalizedStatus(a.status) || 'unknown');
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});
  const statusColorMap = {
    approved: '#1E8E5A',
    completed: '#1E8E5A',
    pending: '#B26E00',
    scheduled: '#B26E00',
    pending_clerk: '#B26E00',
    pending_officer: '#B26E00',
    pending_final: '#B26E00',
    rejected: '#A22C29',
    cancelled: '#A22C29',
    'no-show': '#A22C29',
    rescheduled: '#2E5AAC',
    deferred: '#2E5AAC',
  };
  const statusData = Object.entries(statusCounts).map(([name, value]) => ({
    name,
    value,
    color: statusColorMap[name] || '#6b7280',
  }));

  // Extract pagination meta from backend response (with safe fallbacks)
  const apptsMeta = apptsResponse?.data?.pagination || {};
  const totalScheduledAppts = apptsMeta?.total ?? appointmentsRaw?.length ?? 0;
  const currentApptsPage = apptsMeta?.page ?? apptsPage;
  const apptsTotalPages = apptsMeta?.totalPages ?? (
    apptsLimit > 0 ? Math.max(1, Math.ceil((totalScheduledAppts || 0) / apptsLimit)) : 1
  );


  // Build computed model used by UI (replaces MOCK_DATA)
  const DATA = {
    kpis: {
      totalAppointments: { value: totalAppointmentsVal, delta: 0, trend: 'up', sparkline: [] },
      pendingApprovals: { value: pendingApprovalsArr.length, delta: 0, trend: 'down', sparkline: [] },
      completed: { value: completedCount, delta: 0, trend: 'up', sparkline: [] },
      noShows: { value: noShowCount, delta: 0, trend: noShowCount > 0 ? 'up' : 'down', sparkline: [] },
    },
    appointments: appointmentsData,
    pendingApprovals: pendingApprovalsArr.map((a) => ({
      id: a.id,
      name: a.name || a.officer_name || 'Unknown',
      leaveRemaining: a.leaveRemaining || 0,
      leaveConversion: a.type || 'N/A',
    })),
    charts: {
      hourlyData,
      statusData,
    },
  };

  const handleDateRangeChange = (range) => {
    setDateRange(range);
    // Reset dashboard appointments pagination when range changes
    setApptsPage(1);
    console.log('Date range changed to:', range);
  };

  const handleExport = () => {
    console.log('Exporting dashboard data...');
  };

  const handleNewAppointment = () => {
    console.log('Creating new appointment...');
  };

  const handleApprovalSelection = (id) => {
    setSelectedApprovals(prev =>
      prev.includes(id)
        ? prev.filter(item => item !== id)
        : [...prev, id]
    );
  };

  const handleBulkApprove = () => {
    console.log('Bulk approving:', selectedApprovals);
    setSelectedApprovals([]);
  };

  const handleQuickAction = (action, appointmentId) => {
    console.log(`${action} appointment ${appointmentId}`);
  };

  const getStatusColor = (status) => {
    const s = (status || '').toLowerCase();
    switch (s) {
      case 'approved':
      case 'completed': return 'success';
      case 'pending':
      case 'scheduled':
      case 'pending_clerk':
      case 'pending_officer':
      case 'pending_final': return 'warning';
      case 'rejected':
      case 'cancelled':
      case 'no-show': return 'danger';
      case 'rescheduled':
      case 'deferred': return 'secondary';
      default: return 'secondary';
    }
  };

  const getTypeColor = (type) => {
    const t = (type || '').toLowerCase();
    switch (t) {
      case 'leave related': return 'warning';
      case 'orderly room': return 'success';
      case 'medical': return 'danger';
      case 'training': return 'secondary';
      default: return 'secondary';
    }
  };

  return (
    <div className="h-full p-6 space-y-6 overflow-y-auto ">
      {/* Page Header with Background */}
      <div className="relative rounded-2xl overflow-hidden shadow-md">
        {/* Background Banner */}
        <div className="absolute inset-0">
          <img
            src={banner} // replace with your image path
            alt="Dashboard Banner"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent" />
        </div>

        {/* Content */}
        <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 p-6  h-48 lg:h-56 items-end">
          <div>
            <h1 className="text-2xl font-bold text-white">Dashboard</h1>
            <p className="text-gray-200 mt-1">Overview of operations and key metrics</p>
          </div>

          {/* Date Range Picker */}
          <div className="flex justify-center">
            <div className="flex bg-white/90 backdrop-blur-md rounded-lg border border-gray-200 p-1 shadow-sm">
              {['today', '7d', '30d', 'custom'].map((range) => (
                <button
                  key={range}
                  onClick={() => handleDateRangeChange(range)}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${dateRange === range
                    ? 'bg-teal-700 text-white shadow-md'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                >
                  {range === 'today' ? 'Today' : range === '7d' ? '7 Days' : range === '30d' ? '30 Days' : 'Custom'}
                </button>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              variant="secondary"
              onClick={handleExport}
              className="flex items-center gap-2 bg-white/90 backdrop-blur-md"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              Export
            </Button>
          </div>
        </div>
      </div>




      {/* KPI Cards (from backend summary) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        {[
          { key: 'orders', label: 'Orders', bg: 'from-blue-50 to-indigo-50', iconBg: 'bg-blue-600', Icon: DocumentTextIcon },
          { key: 'circulars', label: 'Circulars', bg: 'from-violet-50 to-fuchsia-50', iconBg: 'bg-violet-600', Icon: DocumentIcon },
          { key: 'appointments', label: 'Appointments', bg: 'from-emerald-50 to-green-50', iconBg: 'bg-emerald-600', Icon: CalendarIcon },
          { key: 'forms', label: 'Forms', bg: 'from-amber-50 to-orange-50', iconBg: 'bg-amber-600', Icon: DocumentTextIcon },
          { key: 'payslips', label: 'Payslips', bg: 'from-teal-50 to-cyan-50', iconBg: 'bg-teal-600', Icon: BuildingOffice2Icon },
        ].map(({ key, label, bg, iconBg, Icon }) => (
          <Card key={key} className={`p-6 border border-gray-200 bg-gradient-to-br ${bg}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 uppercase tracking-wide mb-2">
                  {label}
                </p>
                <p className="text-2xl font-bold text-gray-900 mb-1">
                  {kpisLoading ? '—' : (KPI_SUMMARY[key] ?? 0)}
                </p>
              </div>
              <div className={`p-3 rounded-full ${iconBg}`}>
                <Icon className="h-6 w-6 text-white" />
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column - Appointments & Charts */}
        <div className="lg:col-span-8 space-y-6">
          {/* Scheduled Appointments */}
          <Card className="border border-gray-200">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                  Scheduled Appointments
                </h2>
                <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                  {apptsLoading ? '—' : totalScheduledAppts}  Scheduled 
                </Badge>
              </div>

              {/* Appointments Table */}
              <div className="overflow-x-auto">
                <table className="w-full border border-teal-600">
                  <thead>
                    <tr className="bg-teal-700 border-b border-teal-600 dark:border-teal-600 divide-x divide-teal-600 dark:divide-teal-600">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-white dark:text-white">Date/Time</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-white dark:text-white">Officer</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-white dark:text-white">Type</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-white dark:text-white">Status</th>
                      
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-teal-600 divide-teal-600">
                    {apptsLoading ? (
                      <tr>
                        <td colSpan="4" className="py-6 text-center">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-primary mx-auto"></div>
                        </td>
                      </tr>
                    ) : DATA.appointments.map((appointment) => (
                      <tr key={appointment.id} className="border-b border-teal-600 dark:border-teal-600 hover:bg-gray-50 dark:hover:bg-gray-800 dark:text-white divide-x divide-teal-600 dark:divide-teal-600">
                        <td className="py-3 px-4">
                          <div className="flex flex-col">
                            <span className={`font-medium ${appointment.isNow ? 'text-teal-700' : 'text-gray-900 dark:text-white'}`}>
                              {formatDate(appointment.appointment_date) || ''}
                            </span>
                            {(appointment.appointment_time || appointment.time) && (
                              <span className="text-xs text-gray-600 dark:text-gray-300 mt-1">
                                {appointment.appointment_time || appointment.time}
                              </span>
                            )}
                            {appointment.isNow && (
                              <span className="text-xs text-teal-700 bg-teal-700/10 px-2 py-1 rounded-full mt-1 w-fit">
                                NOW
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">{`${appointment.first_name || ''} ${appointment.last_name || ''}`.trim() || appointment.name || appointment.officer_name}</p>
                            <div className="text-sm text-gray-500 flex gap-3">
                              {appointment.phone_number && (
                                <span>{formatPhoneForDisplay(appointment.phone_number)}</span>
                              )}
                              {(appointment.badge_number || appointment.badge) && (
                                <span className="font-mono">{appointment.badge_number || appointment.badge}</span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant={getTypeColor(appointment.request_type || appointment.type)}>
                            {appointment.request_type || appointment.type || '—'}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant={getStatusColor(appointment.status)}>
                            {(appointment.status || '').replaceAll('_', ' ') || '—'}
                          </Badge>
                        </td>
                        
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* Pagination Footer */}
                <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mt-3 px-1 pb-1">
                  <div>
                    {typeof totalScheduledAppts === 'number' && totalScheduledAppts > 0 && (
                      <span>
                        {`Page ${currentApptsPage} of ${apptsTotalPages} • ${totalScheduledAppts} total`}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      className={`px-3 py-1 rounded border ${currentApptsPage > 1 ? 'hover:bg-gray-100 dark:hover:bg-gray-700' : 'opacity-50 cursor-not-allowed'}`}
                      onClick={() => currentApptsPage > 1 && setApptsPage((p) => Math.max(1, p - 1))}
                      disabled={currentApptsPage <= 1}
                    >
                      Previous
                    </button>
                    <span>
                      Page {currentApptsPage} / {apptsTotalPages}
                    </span>
                    <button
                      className={`px-3 py-1 rounded border ${currentApptsPage < apptsTotalPages ? 'hover:bg-gray-100 dark:hover:bg-gray-700' : 'opacity-50 cursor-not-allowed'}`}
                      onClick={() => currentApptsPage < apptsTotalPages && setApptsPage((p) => p + 1)}
                      disabled={currentApptsPage >= apptsTotalPages}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Right Column - Pending Approvals */}
        <div className="lg:col-span-4">
          <div className="lg:col-span-2">
            <Card className="border border-gray-200 ">
              <div className="p-6">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                  Appointments by Hour
                </h3>
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={DATA.charts.hourlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="hour" stroke="#6b7280" fontSize={12} />
                    <YAxis stroke="#6b7280" fontSize={12} />
                    <Tooltip />
                    <Area
                      type="monotone"
                      dataKey="avg7d"
                      stroke="#9ca3af"
                      fill="#9ca3af"
                      fillOpacity={0.3}
                      name="7-Day Average"
                    />
                    <Area
                      type="monotone"
                      dataKey="today"
                      stroke="#0F766E"
                      fill="#0F766E"
                      fillOpacity={0.6}
                      name="Today"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>
          {/* Status Distribution */}
          <div className="lg:col-span-1 mt-6">
            <Card className="border border-gray-200">
              <div className="p-6">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                  Status Distribution
                </h3>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={DATA.charts.statusData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {DATA.charts.statusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;

